"use client";

import { navItems } from "@/data/items";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import ServicesDropdown from "./menu";
import { Button } from "./ui/button";
import { useUser } from "@/context/user-context";
import Marquee from "./shared/Marquee";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  const pathname = usePathname();
  const { isAuthenticated, user } = useUser();

  return (
    <nav className="sticky top-0 z-40 w-full border-b bg-white py-2">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3 group">
            <div className="h-10 flex items-center justify-center">
              <img
                src="/logo-logo.jpg"
                alt="Hexad Logo"
                className="h-full object-cover"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              href="/"
              className={`nav-button ${pathname === "/" && "active"}`}
            >
              Home
            </Link>
            <Link
              href="/about"
              className={`nav-button ${pathname === "/about" && "active"}`}
            >
              About Us
            </Link>
            <Link
              href="/team"
              className={`nav-button ${pathname === "/team" && "active"}`}
            >
              Team
            </Link>
            <ServicesDropdown />
            <Link
              href="/projects"
              className={`nav-button ${pathname === "/projects" && "active"}`}
            >
              Projects
            </Link>
            <Link
              href="/why-choose-us"
              className={`nav-button ${
                pathname === "/why-choose-us" && "active"
              }`}
            >
              Why Choose Us
            </Link>
            <Link
              href="/contact"
              className={`nav-button ${pathname === "/contact" && "active"}`}
            >
              Contact
            </Link>
          </div>

          {/* Desktop Right Elements */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* <button className="cursor-pointer text-gray-600 hover:text-gray-900 p-2 transition-colors">
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m21 21-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
                />
              </svg>
            </button> */}
            {isAuthenticated ? (
              <Link href="/profile">
                <div className="flex items-center justify-center w-10 h-10 bg-cyan-600 text-white rounded-full border-3 border-green-500 p-2">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              </Link>
            ) : (
              <Link href={"/auth/login"}>
                <Button className="px-5 py-2">Login</Button>
              </Link>
            )}
            <Button
              onClick={() => window.location.replace("/request-quote")}
              variant="outline"
              className="bg-cyan-600 text-white"
            >
              Request a Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Sidebar */}
          <div className="relative ml-auto h-screen w-full max-w-xs sm:max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-white">
              <Link
                href="/"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                  <img
                    src="/logo-logo.jpg"
                    alt="Hexad Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-gray-900 text-base sm:text-lg">
                    Hexad
                  </span>
                  <span className="text-xs text-gray-500 -mt-1">
                    Humanity Counts
                  </span>
                </div>
              </Link>

              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6 space-y-6">
                {/* Navigation */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 px-4 uppercase">
                    Navigation
                  </h3>

                  {navItems.slice(0, 6).map((item) => {
                    const isActive = window.location.pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center px-4 py-3 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all group ${
                          isActive && "border border-blue-500"
                        }`}
                      >
                        <span className="mr-3">{item.icon}</span>
                        <span className="flex-1">{item.label}</span>
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <div className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 px-4 uppercase">
                    Quick Actions
                  </h3>

                  {navItems.slice(6).map((item) => {
                    const isActive = window.location.pathname === item.href;

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center px-4 py-3 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all group ${
                          isActive && "border border-blue-500"
                        }`}
                      >
                        <span className="mr-3">{item.icon}</span>
                        <span className="flex-1">{item.label}</span>
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="shrink-0 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 space-y-3">
              <Button className="w-full bg-linear-to-r from-cyan-600 to-cyan-700 text-white py-3">
                Request a Quote
              </Button>
              {isAuthenticated ? (
                <Link href="/profile">
                  <div className="flex items-center justify-center gap-3 p-3 bg-white rounded-lg">
                    <div className="flex items-center justify-center w-10 h-10 bg-cyan-600 text-white rounded-full border-3 border-green-500 p-2">
                      {user?.firstName?.[0]}
                      {user?.lastName?.[0]}
                    </div>
                    <span>{user?.firstName}</span>
                  </div>
                </Link>
              ) : (
                <Button variant="outline" className="w-full py-3">
                  <Link href="/auth/login" onClick={() => setIsOpen(false)}>
                    Login to Account
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="hidden lg:flex max-w-5xl mx-auto">
        <Marquee />
      </div>
    </nav>
  );
}
